/**
 * Unified Documents Page - Document Engine
 * Combined document generation and library management with tabbed interface
 */
import React, { useState, useEffect } from 'react';
import { BRAND, COLORS, SPACING, TYPOGRAPHY, RADIUS, SHADOWS } from '../../config/branding';
import { Button, LoadingSpinner, ErrorState, EmptyState, useToast } from '../../components/ui';
import { useDocumentGenerator } from '../../hooks/useDocumentGenerator';
import documentService from '../../api/documentService';
import ISOStandardSelector from '../../components/documents/ISOStandardSelector';

function DocumentsUnified() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const userRole = localStorage.getItem('userRole');
  const toast = useToast();

  // Clients should only see the library, not generate documents
  const [activeTab, setActiveTab] = useState(userRole === 'client' ? 'library' : 'generate');

  // Document generation state
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [selectedStandardId, setSelectedStandardId] = useState(1);
  const [documentType, setDocumentType] = useState('level1_manual');
  const [companies, setCompanies] = useState([]);

  // Form/Procedure specific fields
  const [documentName, setDocumentName] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [documentPurpose, setDocumentPurpose] = useState('');
  const [scope, setScope] = useState('');
  const [timeline, setTimeline] = useState('6');

  // Document library state
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const { generateDocument, generateLevel1Manual, generateForm, generateProcedure, loading: generating } = useDocumentGenerator();

  useEffect(() => {
    loadCompanies();
    loadDocuments();

    // Set company ID based on user role
    if (user.role === 'client') {
      setSelectedCompanyId(user.company_id);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'library') {
      loadDocuments();
    }
  }, [activeTab, filter]);

  const loadCompanies = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${import.meta.env.VITE_API_URL || 'http://localhost:8000/api'}/companies/my-clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      setCompanies(data);
      if (data.length > 0 && !selectedCompanyId) {
        setSelectedCompanyId(data[0].id);
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  const loadDocuments = async () => {
    if (!selectedCompanyId) return;

    try {
      setLoading(true);
      const filterType = filter === 'all' ? null : filter;
      const response = await documentService.listDocuments(selectedCompanyId, filterType, 100);
      if (response && response.documents) {
        setDocuments(response.documents);
      }
    } catch (error) {
      console.error('Failed to load documents:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    if (!selectedCompanyId || !selectedStandardId) {
      toast.error('Please select a company and ISO standard');
      return;
    }

    try {
      let result;

      if (documentType === 'level1_manual') {
        result = await generateLevel1Manual(selectedCompanyId, selectedStandardId, { scope });
      } else if (documentType === 'form') {
        if (!documentName || !documentNumber) {
          toast.error('Please provide form name and number');
          return;
        }
        result = await generateForm({
          companyId: selectedCompanyId,
          isoStandardId: selectedStandardId,
          formName: documentName,
          formNumber: documentNumber,
          formPurpose: documentPurpose
        });
      } else if (documentType === 'procedure') {
        if (!documentName || !documentNumber) {
          toast.error('Please provide procedure name and number');
          return;
        }
        result = await generateProcedure({
          companyId: selectedCompanyId,
          isoStandardId: selectedStandardId,
          procedureName: documentName,
          procedureNumber: documentNumber,
          procedureScope: scope
        });
      } else {
        result = await generateDocument({
          company_id: selectedCompanyId,
          iso_standard_id: selectedStandardId,
          document_type: documentType,
          additional_params: { scope, timeline }
        });
      }

      if (result) {
        toast.success(' Document generated successfully!');
        // Auto-download
        if (result.download_url) {
          await documentService.triggerDownload(result.document_id, selectedCompanyId);
        }
        // Refresh library if on library tab
        if (activeTab === 'library') {
          loadDocuments();
        }
      }
    } catch (error) {
      toast.error(` Generation failed: ${error.message}`);
    }
  };

  const handleDownload = async (documentId) => {
    try {
      await documentService.triggerDownload(documentId, selectedCompanyId);
      toast.success('Download started!');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      await documentService.deleteDocument(documentId, selectedCompanyId);
      toast.success('Document deleted');
      loadDocuments();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = searchTerm === '' ||
      (doc.display_name || doc.filename).toLowerCase().includes(searchTerm.toLowerCase()) ||
      doc.document_type.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const DOCUMENT_TYPE_LABELS = {
    level1_manual: 'Level-1 Manual',
    implementation_manual: 'Implementation Manual',
    form: 'Form',
    procedure: 'Procedure',
    implementation_guide: 'Implementation Guide',
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '30px' }}>
        <h2>{BRAND.name} Document Engine</h2>
        <p style={{ color: '#666', marginTop: '8px' }}>
          {userRole === 'super_admin' && 'Generate professional ISO documents instantly using Claude AI'}
          {userRole === 'third_party_admin' && 'Generate ISO documents for your clients using Claude AI'}
          {userRole === 'client' && 'View and download your ISO documents'}
        </p>
      </div>

      {/* Tab Navigation */}
      <div style={{ marginBottom: '30px', borderBottom: '2px solid #e0e0e0' }}>
        <div style={{ display: 'flex', gap: '5px' }}>
          {/* Only show Generate tab for admins, not for clients */}
          {userRole !== 'client' && (
            <button
              onClick={() => setActiveTab('generate')}
              style={{
                padding: '12px 24px',
                backgroundColor: activeTab === 'generate' ? 'white' : 'transparent',
                color: activeTab === 'generate' ? '#D4AF37' : '#666',
                border: 'none',
                borderBottom: activeTab === 'generate' ? '3px solid #D4AF37' : '3px solid transparent',
                cursor: 'pointer',
                fontSize: '15px',
                fontWeight: activeTab === 'generate' ? 'bold' : 'normal',
                transition: 'all 0.2s',
              }}
            >
              Generate Documents
            </button>
          )}
          <button
            onClick={() => setActiveTab('library')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'library' ? 'white' : 'transparent',
              color: activeTab === 'library' ? '#D4AF37' : '#666',
              border: 'none',
              borderBottom: activeTab === 'library' ? '3px solid #D4AF37' : '3px solid transparent',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: activeTab === 'library' ? 'bold' : 'normal',
              transition: 'all 0.2s',
            }}
          >
            Document Library
          </button>
        </div>
      </div>

      {/* Generate Documents Tab - Only accessible to admins */}
      {activeTab === 'generate' && userRole !== 'client' && (
        <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#2c3e50' }}>Generate ISO Documents</h3>

          {/* Company Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Company:</label>
            <select
              value={selectedCompanyId || ''}
              onChange={(e) => setSelectedCompanyId(Number(e.target.value))}
              disabled={user.role === 'client'}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid #ddd',
              }}
            >
              {companies.map(company => (
                <option key={company.id} value={company.id}>{company.name}</option>
              ))}
            </select>
          </div>

          {/* ISO Standard Selection */}
          <div style={{ marginBottom: '20px' }}>
            <ISOStandardSelector
              value={selectedStandardId}
              onChange={setSelectedStandardId}
            />
          </div>

          {/* Document Type Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Document Type:</label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid #ddd',
              }}
            >
              <option value="level1_manual">Level-1 Manual</option>
              <option value="implementation_manual">Implementation Manual</option>
              <option value="form">Form / Template</option>
              <option value="procedure">Procedure (SOP)</option>
              <option value="implementation_guide">Implementation Guide</option>
            </select>
          </div>

          {/* Conditional Fields for Forms and Procedures */}
          {(documentType === 'form' || documentType === 'procedure') && (
            <>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  {documentType === 'form' ? 'Form Name' : 'Procedure Name'} <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  value={documentName}
                  onChange={(e) => setDocumentName(e.target.value)}
                  placeholder={documentType === 'form' ? 'e.g., Risk Assessment Form' : 'e.g., Document Control Procedure'}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                  }}
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  {documentType === 'form' ? 'Form Number' : 'Procedure Number'} <span style={{ color: 'red' }}>*</span>
                </label>
                <input
                  type="text"
                  value={documentNumber}
                  onChange={(e) => setDocumentNumber(e.target.value)}
                  placeholder="e.g., 0001"
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    border: '1px solid #ddd',
                  }}
                />
              </div>

              {documentType === 'form' && (
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Form Purpose:</label>
                  <textarea
                    value={documentPurpose}
                    onChange={(e) => setDocumentPurpose(e.target.value)}
                    placeholder="Describe the purpose of this form"
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px',
                      borderRadius: '6px',
                      border: '1px solid #ddd',
                    }}
                  />
                </div>
              )}
            </>
          )}

          {/* Scope Field for Manuals and Procedures */}
          {(documentType === 'level1_manual' || documentType === 'procedure') && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Scope:</label>
              <textarea
                value={scope}
                onChange={(e) => setScope(e.target.value)}
                placeholder="e.g., Software development services"
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                }}
              />
            </div>
          )}

          {/* Timeline for Implementation documents */}
          {(documentType === 'implementation_manual' || documentType === 'implementation_guide') && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Implementation Timeline (months):</label>
              <input
                type="number"
                value={timeline}
                onChange={(e) => setTimeline(e.target.value)}
                min="3"
                max="24"
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                }}
              />
            </div>
          )}

          {/* Generate Button */}
          <Button
            onClick={handleGenerate}
            loading={generating}
            fullWidth
            size="lg"
            variant="primary"
          >
            {generating ? 'Generating...' : 'Generate Document'}
          </Button>

          {/* Billing Info */}
          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#e8f5e9',
            borderRadius: '6px',
            fontSize: '13px',
            color: '#2e7d32',
          }}>
            <strong>Billing Information:</strong>
            <br />
            <br />
            <strong>Manuals</strong> (Level-1 & Implementation):
            <br />
            • First 5 manuals: <strong>FREE</strong>
            <br />
            • Additional manuals: <strong>£25 each</strong>
            <br />
            <br />
            <strong>Procedures:</strong>
            <br />
            • First 50 procedures: <strong>FREE</strong>
            <br />
            • Additional procedures: <strong>£1.50 each</strong>
            <br />
            <br />
            <strong>Forms:</strong>
            <br />
            • First 50 forms: <strong>FREE</strong>
            <br />
            • Additional forms: <strong>£1.50 each</strong>
            <br />
            <br />
            <strong>Implementation Guides:</strong>
            <br />
            • First 26 guides: <strong>FREE</strong>
            <br />
            <br />
            <strong>Super Admins:</strong> <strong>Unlimited FREE</strong> (all document types)
          </div>
        </div>
      )}

      {/* Document Library Tab */}
      {activeTab === 'library' && (
        <div style={{ padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '12px' }}>
          <h3 style={{ marginTop: 0, marginBottom: '20px', color: '#2c3e50' }}>Document Library</h3>

          {/* Filters and Search */}
          <div style={{ marginBottom: '20px', display: 'flex', gap: '15px' }}>
            <div style={{ flex: 1 }}>
              <input
                type="text"
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                }}
              >
                <option value="all">All Document Types</option>
                <option value="level1_manual">Level-1 Manuals</option>
                <option value="implementation_manual">Implementation Manuals</option>
                <option value="form">Forms</option>
                <option value="procedure">Procedures</option>
                <option value="implementation_guide">Implementation Guides</option>
              </select>
            </div>
          </div>

          {/* Document List */}
          {loading ? (
            <LoadingSpinner size="lg" text="Loading documents..." />
          ) : filteredDocuments.length === 0 ? (
            <EmptyState
              title="No documents found"
              message={searchTerm ? "No documents match your search criteria." : "You haven't generated any documents yet."}
            />
          ) : (
            <div style={{ backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead style={{ backgroundColor: '#f5f5f5' }}>
                  <tr>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Document</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Type</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Size</th>
                    <th style={{ padding: '12px', textAlign: 'left', borderBottom: '2px solid #e0e0e0' }}>Modified</th>
                    <th style={{ padding: '12px', textAlign: 'right', borderBottom: '2px solid #e0e0e0' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDocuments.map((doc) => (
                    <tr key={doc.document_id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                      <td style={{ padding: '12px' }}>
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{doc.display_name || doc.filename}</div>
                          <div style={{ fontSize: '12px', color: '#666' }}>{doc.filename}</div>
                        </div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 12px',
                          backgroundColor: '#e3f2fd',
                          color: '#1565c0',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                        }}>
                          {DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}
                        </span>
                      </td>
                      <td style={{ padding: '12px', color: '#666' }}>{formatFileSize(doc.file_size)}</td>
                      <td style={{ padding: '12px', color: '#666' }}>{new Date(doc.modified).toLocaleDateString()}</td>
                      <td style={{ padding: '12px', textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: SPACING.sm, justifyContent: 'flex-end' }}>
                          <Button
                            onClick={() => handleDownload(doc.document_id)}
                            size="sm"
                            variant="primary"
                          >
                            Download
                          </Button>
                          <Button
                            onClick={() => handleDelete(doc.document_id)}
                            size="sm"
                            variant="error"
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default DocumentsUnified;
